import { useState } from "react";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isProblem, PROBLEM_TYPES } from "@jbnl/api-client";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { DuplicaatWaarschuwingDialog } from "./DuplicaatWaarschuwing";
import {
  useAddWedstrijdbaan,
  useDuplicaatWaarschuwing,
  useKlassen,
  useRemoveWedstrijdbaan,
  type Wedstrijddag,
} from "./useWedstrijden";

/**
 * The duplicate refusal is shown as a dialog instead, not repeated as plain text below the form.
 */
function bepaalFout(addFout: Error | null, removeFout: Error | null): Error | null {
  return (isProblem(addFout, PROBLEM_TYPES.wedstrijddagMogelijkDuplicaat) ? null : addFout)
    ?? removeFout;
}

/**
 * No aantal-veld: each baan is something ploegen stand on, so two are the klasse added twice
 * (#60). The label is the server's, because adding or removing one can rename the others.
 */
export function Banen({ seizoenId, dag }: { seizoenId: string; dag: Wedstrijddag }) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const { data: klassen } = useKlassen();
  const add = useAddWedstrijdbaan(seizoenId);
  const remove = useRemoveWedstrijdbaan(seizoenId);
  const duplicaat = useDuplicaatWaarschuwing<{ dagId: string; klasseId: string }>();

  const fout = bepaalFout(add.error, remove.error);
  const [klasseId, setKlasseId] = useState("");

  const gekozen = klasseId || klassen?.[0]?.id || "";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t("banen.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {dag.wedstrijdbanen.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("banen.leeg")}</p>
          ) : (
            <ul aria-label={t("banen.lijstLabel")} className="flex flex-wrap gap-2">
              {dag.wedstrijdbanen.map((baan) => (
                <BaanChip
                  key={baan.id}
                  baan={baan}
                  verwijderBezig={remove.isPending}
                  onVerwijder={() => remove.mutate(baan.id)}
                />
              ))}
            </ul>
          )}

          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (!gekozen) return;
              const waarden = { dagId: dag.id, klasseId: gekozen };
              add.mutate(waarden, { onError: (error) => duplicaat.vangOp(waarden, error) });
            }}
          >
            <div className="grid min-w-52 flex-1 gap-2">
              {/* A select, and one of the two the standard sanctions: four klassen, the same four
                  every year, and never a list of records anyone browses. */}
              <Label htmlFor="baan-klasse">{t("banen.klasse")}</Label>
              <Select
                id="baan-klasse"
                value={gekozen}
                onChange={(event) => setKlasseId(event.target.value)}
              >
                {(klassen ?? []).map((klasse) => (
                  <option key={klasse.id} value={klasse.id}>
                    {klasse.name}
                  </option>
                ))}
              </Select>
            </div>
            <Button type="submit" variant="secondary" disabled={add.isPending || !gekozen}>
              <Plus />
              {add.isPending ? t("common:actions.busy") : t("banen.toevoegen")}
            </Button>
          </form>

          {fout && <p className="text-sm text-destructive">{fout.message}</p>}

          <p className="text-xs text-muted-foreground">{t("banen.uitleg")}</p>
        </CardContent>
      </Card>

      <DuplicaatWaarschuwingDialog
        bericht={duplicaat.bericht}
        onOpenChange={(open) => !open && duplicaat.sluit()}
        onDoorgaan={() => add.mutate(duplicaat.doorgaan())}
      />
    </>
  );
}

/**
 * Confirmed because removing a baan renumbers the rest of its klasse.
 */
function BaanChip({
  baan,
  verwijderBezig,
  onVerwijder,
}: {
  baan: Wedstrijddag["wedstrijdbanen"][number];
  verwijderBezig: boolean;
  onVerwijder: () => void;
}) {
  const { t } = useTranslation("wedstrijden");

  return (
    <li className="flex items-center gap-1 rounded-md border bg-background py-1 pr-1 pl-3 text-sm">
      <span className="font-medium">{baan.label}</span>
      <ConfirmDelete
        trigger={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("banen.verwijderKnop", { baan: baan.label })}
            disabled={verwijderBezig}
          >
            <X />
          </Button>
        }
        title={t("banen.verwijderVraag", { baan: baan.label })}
        description={t("banen.verwijderDescription")}
        confirmLabel={t("banen.verwijderConfirm")}
        onConfirm={onVerwijder}
      />
    </li>
  );
}
